import theme from "antd/lib/theme";
import { useTranslation } from "react-i18next";

type SkillProgressBadgeProps = {
    profile: { status: "acquired" | "assigned" | "unassigned"; level: number }[] | null;
} & React.ComponentProps<"div">;

export const useSkillStatusColor = () => {
    const { token } = theme.useToken();

    return (status?: "acquired" | "assigned" | "unassigned") => {
        const colors = {
            acquired: token.colorPrimaryActive,
            assigned: token.colorPrimaryBorderHover,
            unassigned: token.colorFill,
        } as const;

        return status ? colors[status] : colors.unassigned;
    };
};

export const SkillProgressBadge = ({ profile, ...props }: SkillProgressBadgeProps) => {
    const [t] = useTranslation();
    const getColor = useSkillStatusColor();
    const size = 30;
    const barWidth = 8;
    const spacing = 3; // Espacement entre les barres
    const padding = 0; // Padding autour des barres

    if (!profile || profile.length === 0) {
        return <span className="text-ant-secondary">-</span>;
    }

    // Extract status for each level from profile array
    const level1Status = profile.find(item => item.level === 1)?.status ?? "unassigned";
    const level2Status = profile.find(item => item.level === 2)?.status ?? "unassigned";
    const level3Status = profile.find(item => item.level === 3)?.status ?? "unassigned";

    // Hauteurs des barres : plus petite à gauche, plus grande à droite
    const bar1Height = 8; // Level 1 : la plus petite
    const bar2Height = 16; // Level 2 : moyenne
    const bar3Height = 24; // Level 3 : la plus grande

    // Position X de chaque barre (alignées en bas)
    const availableWidth = size - 2 * padding;
    const totalBarsWidth = 3 * barWidth + 2 * spacing;
    const startX = padding + (availableWidth - totalBarsWidth) / 2;
    const x1 = startX;
    const x2 = x1 + barWidth + spacing;
    const x3 = x2 + barWidth + spacing;

    // Position Y : toutes alignées en bas
    const bottomY = size - padding;
    const y1 = bottomY - bar1Height;
    const y2 = bottomY - bar2Height;
    const y3 = bottomY - bar3Height;

    const bars = [
        {
            x: x1,
            y: y1,
            width: barWidth,
            height: bar1Height,
            color: getColor(level1Status),
            level: 1,
            status: level1Status,
        },
        {
            x: x2,
            y: y2,
            width: barWidth,
            height: bar2Height,
            color: getColor(level2Status),
            level: 2,
            status: level2Status,
        },
        {
            x: x3,
            y: y3,
            width: barWidth,
            height: bar3Height,
            color: getColor(level3Status),
            level: 3,
            status: level3Status,
        },
    ];

    const title = bars
        .map(
            bar =>
                `${t("features.dashboards.details.skillsProgress.legendLevel" + bar.level)}: ${t(`features.dashboards.details.skillsProgress.${bar.status}`)}`,
        )
        .join(", ");

    return (
        <div title={title} {...props}>
            <svg className="size-[1.5em]" viewBox={`0 0 ${size} ${size}`}>
                {bars.map((bar, index) => (
                    <rect
                        key={index}
                        x={bar.x}
                        y={bar.y}
                        width={bar.width}
                        height={bar.height}
                        fill={bar.color}
                        rx={2}
                    />
                ))}
            </svg>
        </div>
    );
};
